import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ECellValueDisplayMode,
  ECellValueType,
  EExcelTablePaginationType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelTable from 'components/ui/excel-table';
import { ICellRendererParams } from 'ag-grid-community';
import { BindingRow, BindingTableProps } from './types';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { contractApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import {
  BindingDataContractResponseDto,
  BindingDataResponseDto,
  ContractResponseDtoContractStatusEnum,
} from 'libs/pinnacle-sdk';
import { isEmpty } from 'lodash';
import { Button, message } from 'antd';
import DocumentForwardIcon from 'assets/icons/document-forward';
import ExportV2Icon from 'assets/icons/export-v2';
import { getContractStatusLabel, realNumberToPercentage } from 'utils';
import { useNavigate } from 'react-router';
import { constructContractDetailPath, EPageFrom } from 'constants/path';
import { validateBindingRow } from 'components/features/binding/binding-table/forms';
import { handleError } from 'utils/form';

const BindingTable: React.FC<BindingTableProps> = ({
  submissionId,
  handleBindingContract,
  currency,
  handleSlipUpload,
  handleCloseContractDetail,
  handleSlipUploadRowClick,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [expandedLayers, setExpandedLayers] = useState<Record<number, boolean>>(
    {}
  );
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const {
    data: bindingData,
    isLoading: isLoadingBindingData,
    refetch: refetchBindingData,
  } = useQuery([EQueryKeys.BINDING_DATA, submissionId, currency], () =>
    contractApi.getBindingData({
      getBindingDataQueryDto: {
        submissionId,
        currency,
        includeBinder: true,
        includeBroker: true,
        includeReinsured: true,
      },
    })
  );

  const updateContractStatusMutation = useMutation(
    (payload: {
      contractId: string;
      contractStatus: ContractResponseDtoContractStatusEnum;
    }) =>
      contractApi.updateContractById({
        contractId: payload.contractId,
        updateContractPayloadDto: {
          contractStatus: payload.contractStatus,
        },
      }),
    {
      onMutate: () => {
        setIsUpdatingStatus(true);
      },
      onError: async (error: { response: Response }) => {
        setIsUpdatingStatus(false);
        handleError({
          error,
          defaultMessage: t('binding.updateContractError'),
        });
      },
      onSettled: () => {
        setIsUpdatingStatus(false);
        message.success(t('binding.contractStatusUpdated'));
        refetchBindingData();
      },
    }
  );

  // Auto-expand all layers when data loads
  useEffect(() => {
    if (bindingData?.length) {
      // Create an object with all layers expanded
      const allExpandedLayers = bindingData.reduce(
        (acc, layer) => {
          acc[layer.layerNumber] = true;
          return acc;
        },
        {} as Record<number, boolean>
      );

      setExpandedLayers(allExpandedLayers);
    }
  }, [bindingData]);

  const generateSummaryRows = useCallback((layer: BindingDataResponseDto) => {
    return {
      id: `${layer.layerNumber}-SUMMARY`,
      layerNumber: layer.layerNumber,
      limitType: layer.limitType,
      limit: parseFloat(layer.limit),
      excess: layer.excess ? parseFloat(layer.excess) : undefined,
      layerPremium: parseFloat(layer.premium),
      totalDeductions: realNumberToPercentage(layer.totalDeductions),
      binderShareQuoted: realNumberToPercentage(layer.binderShareQuoted),
      shareOfLimitQuoted: layer.shareOfLimitQuoted
        ? parseFloat(layer.shareOfLimitQuoted)
        : undefined,
      shareOfPremiumQuoted: layer.shareOfPremiumQuoted
        ? parseFloat(layer.shareOfPremiumQuoted)
        : undefined,
      binderShareSigned: layer.binderShareSigned
        ? realNumberToPercentage(layer.binderShareSigned)
        : undefined,
      shareOfLimitSigned: layer.shareOfLimitSigned
        ? parseFloat(layer.shareOfLimitSigned)
        : undefined,
      shareOfPremiumSigned: layer.shareOfPremiumSigned
        ? parseFloat(layer.shareOfPremiumSigned)
        : undefined,
      isGroupRow: true,
      groupId: layer.layerNumber,
    };
  }, []);

  const generateLayerGroupDataRows = useCallback(
    (contract: BindingDataContractResponseDto) => {
      return {
        id: contract.contractId,
        layerNumber: contract.layerNumber,
        limitType: contract.limitType,
        limit: parseFloat(contract.limit),
        excess: contract.excess ? parseFloat(contract.excess) : undefined,
        layerPremium: parseFloat(contract.premium),
        totalDeductions: realNumberToPercentage(contract.totalDeductions),
        reinsured: contract.reinsured?.name,
        sourceBroker: contract.broker?.name,
        binder: contract.binder?.binderName,
        binderShareQuoted: contract.binderShareQuoted
          ? realNumberToPercentage(contract.binderShareQuoted)
          : 0,
        shareOfLimitQuoted: contract.binderShareQuoted
          ? parseFloat(contract.binderShareQuoted) * parseFloat(contract.limit)
          : 0,
        shareOfPremiumQuoted: contract.binderShareQuoted
          ? parseFloat(contract.binderShareQuoted) *
            parseFloat(contract.premium)
          : 0,
        status: contract.contractStatus,
        binderShareSigned: contract.binderShareSigned
          ? realNumberToPercentage(contract.binderShareSigned)
          : undefined,
        shareOfLimitSigned: contract.binderShareSigned
          ? parseFloat(contract.binderShareSigned) * parseFloat(contract.limit)
          : undefined,
        shareOfPremiumSigned: contract.binderShareSigned
          ? parseFloat(contract.binderShareSigned) *
            parseFloat(contract.premium)
          : undefined,
        isSlipUploaded: contract.isSlipUploaded,
      };
    },
    []
  );

  const tableData = useMemo(() => {
    const layerGroups: Record<number, BindingRow[]> = {};
    const layerSummaries: Record<number, BindingRow> = {};

    if (bindingData && !isEmpty(bindingData)) {
      bindingData.forEach((layer) => {
        if (!layerGroups[layer.layerNumber]) {
          layerGroups[layer.layerNumber] = [];
          layerSummaries[layer.layerNumber] = generateSummaryRows(layer);
        }

        if (layer.contracts && layer.contracts.length > 0) {
          layer.contracts.forEach((contract) => {
            const layerGroupDataRow = generateLayerGroupDataRows(contract);
            layerGroups[layer.layerNumber].push(layerGroupDataRow);
          });
        }
      });
    } else {
      return [];
    }

    let result: BindingRow[] = [];

    const sortedLayerNumbers = Object.keys(layerGroups)
      .map(Number)
      .sort((a, b) => a - b);

    sortedLayerNumbers.forEach((layerNumber) => {
      result.push(layerSummaries[layerNumber]);

      if (expandedLayers[layerNumber]) {
        result = [...result, ...layerGroups[layerNumber]];
      }
    });

    return result;
  }, [
    bindingData,
    generateSummaryRows,
    generateLayerGroupDataRows,
    expandedLayers,
  ]);

  const validateRow = useCallback(
    (row: BindingRow, rowIndex: number, allRows: BindingRow[]) => {
      const errors = validateBindingRow(row, rowIndex, allRows, t);
      return errors;
    },
    [t]
  );

  const toggleExpandLayer = useCallback((layerNum: string | number) => {
    setExpandedLayers((prev) => ({
      ...prev,
      [layerNum]: !prev[Number(layerNum)],
    }));
  }, []);

  const handleMoveToPending = useCallback(
    (contractId: string) => {
      updateContractStatusMutation.mutate({
        contractId,
        contractStatus: ContractResponseDtoContractStatusEnum.Pending,
      });
    },
    [updateContractStatusMutation]
  );

  const handleMoveToQuoted = useCallback(
    (contractId: string) => {
      updateContractStatusMutation.mutate({
        contractId,
        contractStatus: ContractResponseDtoContractStatusEnum.Quoted,
      });
    },
    [updateContractStatusMutation]
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'layerNumber',
        headerName: t('binding.layer'),
        minWidth: 160,
        resizable: true,
        flex: 1,
        editable: false,
        cellRenderer: (params: ICellRendererParams<BindingRow>) => {
          if (!params.data) return '';

          const layerNum = params.data.layerNumber;
          if (!layerNum) return '';

          if (params.data.isGroupRow) {
            return `Layer ${layerNum} Summary`;
          }

          return `Layer ${layerNum}`;
        },
      },
      {
        field: 'status',
        headerName: t('binding.status'),
        minWidth: 200,
        resizable: true,
        flex: 1,
        editable: false,
        cellRenderer: (params: ICellRendererParams<BindingRow>) => {
          if (!params.data) return '';
          return getContractStatusLabel(t)(params.value);
        },
      },
      {
        field: 'limitType',
        headerName: t('binding.limitType'),
        minWidth: 140,
        resizable: true,
        flex: 1,
        editable: false,
        precision: 0,
        cellRenderer: (params: ICellRendererParams<BindingRow>) => {
          if (!params.data) return '';
          return params.value;
        },
      },
      {
        field: 'limit',
        headerName: 'Limit (100%)',
        resizable: true,
        minWidth: 140,
        precision: 0,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
      },
      {
        field: 'excess',
        headerName: 'Excess (100%)',
        minWidth: 140,
        flex: 1,
        resizable: true,
        precision: 0,
        editable: false,
        type: ECellValueType.NUMERIC_COLUMN,
        valueFormatter: (params) => {
          if (!params.value) return '';
          return Number(params.value)?.toLocaleString();
        },
      },
      {
        field: 'layerPremium',
        headerName: 'Layer Premium (100%)',
        minWidth: 170,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        precision: 0,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
      },
      {
        field: 'totalDeductions',
        headerName: 'Total Deductions %',
        minWidth: 150,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: false,
      },
      {
        field: 'reinsured',
        headerName: 'Reinsured',
        minWidth: 130,
        resizable: true,
        flex: 1,
        editable: false,
      },
      {
        field: 'sourceBroker',
        headerName: 'Source Broker',
        minWidth: 140,
        resizable: true,
        flex: 1,
        editable: false,
      },
      {
        field: 'binder',
        headerName: t('binding.binder'),
        minWidth: 200,
        resizable: true,
        flex: 1,
        editable: false,
      },
      {
        field: 'binderShareQuoted',
        headerName: t('binding.binderShareWritten'),
        minWidth: 170,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: false,
      },
      {
        field: 'shareOfLimitQuoted',
        headerName: t('binding.shareOfLimitQuoted'),
        minWidth: 170,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        precision: 0,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
        cellStyle: () => {
          return { backgroundColor: '#FFFFFF' };
        },
      },
      {
        field: 'shareOfPremiumQuoted',
        headerName: t('binding.shareOfPremiumQuoted'),
        minWidth: 190,
        resizable: true,
        flex: 1,
        precision: 0,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
        cellStyle: () => {
          return { backgroundColor: '#FFFFFF' };
        },
      },
      {
        field: 'binderShareSigned',
        headerName: 'Binder Share Signed %',
        minWidth: 170,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        displayMode: ECellValueDisplayMode.PERCENTAGE,
        editable: false,
        precision: 4,
      },
      {
        field: 'shareOfLimitSigned',
        headerName: t('binding.shareOfLimitSigned'),
        minWidth: 170,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
        precision: 0,
      },
      {
        field: 'shareOfPremiumSigned',
        headerName: t('binding.shareOfPremiumSigned'),
        minWidth: 190,
        resizable: true,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        editable: false,
        valueFormatter: (params) => {
          return params.value?.toLocaleString();
        },
        precision: 0,
      },
      {
        field: 'action',
        headerName: t('binding.action'),
        minWidth: 350,
        resizable: true,
        flex: 1,
        editable: false,
        pinned: 'right',
        lockPosition: true,
        suppressMovable: true,
        cellRenderer: (params: ICellRendererParams<BindingRow>) => {
          if (!params.data || params.data.isGroupRow || params.data.isAddRow)
            return '';

          if (!params.data || params.data.isGroupRow) return '';

          const { status } = params.data;

          const isUploadSlipEnabled =
            status === ContractResponseDtoContractStatusEnum.Bound &&
            params.data.isSlipUploaded === false;

          const isBindOrCloseEnabled =
            status === ContractResponseDtoContractStatusEnum.Quoted;

          const isMoveToQuotedEnabled =
            status === ContractResponseDtoContractStatusEnum.Pending;

          if (isUploadSlipEnabled) {
            return (
              <div
                className="flex items-center justify-center w-full h-11"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  type="primary"
                  className="bg-[#1d67b6] w-full justify-center items-center rounded-xl"
                  icon={<ExportV2Icon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleSlipUpload(params.data?.id);
                  }}
                >
                  {t('binding.uploadSlip')}
                </Button>
              </div>
            );
          }

          if (isBindOrCloseEnabled) {
            return (
              <div
                className="flex items-center justify-center w-full h-11 gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  type="primary"
                  className="bg-[#1d67b6] flex-1 justify-center items-center rounded-xl"
                  icon={<DocumentForwardIcon />}
                  iconPosition="end"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleBindingContract(params.data?.id);
                  }}
                >
                  {t('binding.bindOrClose')}
                </Button>
                <Button
                  type="primary"
                  className="bg-[#1d67b6] flex-1 justify-center items-center rounded-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    params.data?.id && handleMoveToPending(params.data.id);
                  }}
                >
                  {t('binding.moveToPending')}
                </Button>
              </div>
            );
          }

          if (isMoveToQuotedEnabled) {
            return (
              <div
                className="flex items-center justify-center w-full h-11"
                onClick={(e) => e.stopPropagation()}
              >
                <Button
                  type="primary"
                  className="bg-[#1d67b6] w-full justify-center items-center rounded-xl"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    params.data?.id && handleMoveToQuoted(params.data.id);
                  }}
                >
                  {t('binding.moveToQuoted')}
                </Button>
              </div>
            );
          }
        },
      },
      {
        field: 'isSlipUploaded',
        hide: true,
      },
    ],
    [
      t,
      handleBindingContract,
      handleSlipUpload,
      handleMoveToPending,
      handleMoveToQuoted,
    ]
  );

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <ExcelTable
        data={tableData}
        columnDefs={columnDefs}
        pagination={false}
        rowKeyColName="id"
        baseTableHeight={490}
        tableHeightBias={320}
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={false}
        includeAddRow={false}
        includeDeleteCol={false}
        onRowClicked={(params) => {
          // Handle the sub-row on click event
          if (!params.data?.isGroupRow && !isUpdatingStatus) {
            const status = params.data?.status;
            const isSlipUploaded = params.data?.isSlipUploaded;

            switch (status) {
              case ContractResponseDtoContractStatusEnum.Bound:
                return isSlipUploaded
                  ? navigate(
                      constructContractDetailPath(params.data?.id || ''),
                      {
                        state: {
                          from: EPageFrom.BINDING_DASHBOARD,
                        },
                      }
                    )
                  : handleSlipUploadRowClick(params.data?.id);

              case ContractResponseDtoContractStatusEnum.Quoted:
                return handleBindingContract(params.data?.id);

              case ContractResponseDtoContractStatusEnum.Cancelled:
              case ContractResponseDtoContractStatusEnum.NotTakenUp:
                return handleCloseContractDetail(params.data?.id);

              case ContractResponseDtoContractStatusEnum.Pending:
                return;
            }
          }
        }}
        loading={isLoadingBindingData}
        animateRows={true}
        groupConfig={{
          groupField: 'layerNumber',
          expandedGroups: expandedLayers,
          onToggleGroup: toggleExpandLayer,
        }}
        validateRow={validateRow}
        paginationType={EExcelTablePaginationType.INFO}
      />
    </div>
  );
};

export default BindingTable;
